import { createSelector } from "@reduxjs/toolkit";
import { CustomerAccountRole, RewardsAccount, RewardsPrice } from "redmond";
import { VXB } from "@capone/common";
import { IStoreState } from "../../../../reducers/types";

import { getCardPaymentRewardsAccount } from "../../../book/reducer/selectors";

const VXB_FLIGHTS_MULTIPLIER = 5;

export const getIsFirstLaunch = (state: IStoreState) =>
  state.flightRewards.isFirstLaunch;

export const getAgentEmail = (state: IStoreState) =>
  state.flightRewards.agentEmail;

export const getRewardsAccounts = (state: IStoreState) =>
  state.flightRewards.rewardsAccounts;

export const getIsVentureX = (state: IStoreState) =>
  getRewardsAccounts(state).some((acc) =>
    acc.productDisplayName.includes("Venture X")
  );

export const getUserSelectedAccountReferenceId = (state: IStoreState) =>
  state.flightRewards.selectedAccountReferenceId;

export const getFetchRewardsAccountsCallState = (state: IStoreState) =>
  state.flightRewards.fetchRewardsAccountsCallState;

export const getRewardsAccountWithLargestValue = createSelector(
  getRewardsAccounts,
  (rewardsAccounts): RewardsAccount | undefined => {
    const sortedAccts = [...rewardsAccounts].sort(
      (prev, current) =>
        current.rewardsCashEquivalent.value - prev.rewardsCashEquivalent.value
    );
    return sortedAccts[0];
  }
);

export const getEligibleRewardsAccountWithLargestValue = createSelector(
  getRewardsAccounts,
  (rewardsAccounts): RewardsAccount | undefined => {
    const sortedAccts = [...rewardsAccounts].sort(
      (prev, current) =>
        current.rewardsCashEquivalent.value - prev.rewardsCashEquivalent.value
    );
    return sortedAccts.filter(
      (account) => account.allowRewardsRedemption ?? true
    )[0];
  }
);

// the rewards account selection doesn't have a default state where none of the available options is selected
export const getDefaultSelectedAccountReferenceId = createSelector(
  getRewardsAccounts,
  (rewardsAccounts): string | undefined => {
    // return the id with largest earn
    if (rewardsAccounts.length > 0) {
      return rewardsAccounts[0].accountReferenceId;
    }

    return undefined;
  }
);

// this is the primary selector used outside the component
// either returns the user selected account id, or the default from above if not present
export const getSelectedAccountReferenceId = createSelector(
  getUserSelectedAccountReferenceId,
  getDefaultSelectedAccountReferenceId,
  (
    userSelectedAccountReferenceId,
    defaultSelectedAccountReferenceId
  ): string | undefined =>
    userSelectedAccountReferenceId ?? defaultSelectedAccountReferenceId
);

export const getSelectedAccount = createSelector(
  getRewardsAccounts,
  getSelectedAccountReferenceId,
  (rewardsAccounts, referenceId): RewardsAccount | undefined =>
    rewardsAccounts.find(
      (account) => account.accountReferenceId === referenceId
    )
);

export const getSelectedAccountIndex = createSelector(
  getRewardsAccounts,
  getSelectedAccountReferenceId,
  (accounts, id) =>
    accounts.findIndex((account) => account.accountReferenceId === id)
);

export const getSelectedAccountReferenceIdIfRedemptionEnabled = createSelector(
  getSelectedAccountReferenceId,
  getSelectedAccount,
  (referenceId, selectedAccount) =>
    selectedAccount?.allowRewardsRedemption ?? true
      ? referenceId || undefined
      : undefined
);
export const getRewardsAccountWithLargestEarnForBanner = createSelector(
  getRewardsAccounts,
  (rewardsAccounts): RewardsAccount => rewardsAccounts[0]
);

export const getEarnForHighestEarnRewardsAccount = createSelector(
  getRewardsAccountWithLargestEarnForBanner,
  (state: IStoreState) => state.flightBook.earnValuesByRewardAcctId,
  (state: IStoreState) => state.flightBook.priceQuote,
  (state: IStoreState) => state.flightBook.rewardsPaymentInFiatCurrency,
  (state: IStoreState) => state.flightBook.creditToApply,
  (state: IStoreState) => getCardPaymentRewardsAccount(state),
  (
    rewardsAccount,
    earnValuesByRewardAcctId,
    priceQuote,
    rewardsPaymentInFiatCurrency,
    creditToApply,
    cardPaymentRewardsAccount
  ): RewardsPrice | undefined => {
    if (rewardsAccount?.customerAccountRole !== CustomerAccountRole.Primary) {
      return undefined;
    }

    if (!rewardsAccount.productDisplayName.includes(VXB)) {
      return undefined;
    }

    if (rewardsAccount.earn.flightsMultiplier !== VXB_FLIGHTS_MULTIPLIER) {
      return undefined;
    }

    if (!cardPaymentRewardsAccount) {
      const earnForAccount =
        earnValuesByRewardAcctId?.[rewardsAccount.accountReferenceId];

      if (!earnForAccount) {
        return undefined;
      }

      return {
        value: earnForAccount.amount,
        currency: rewardsAccount.rewardsBalance.currency,
        currencyDescription:
          rewardsAccount.rewardsBalance.currencyDescription || "miles",
      };
    }

    if (
      cardPaymentRewardsAccount.accountReferenceId !==
      rewardsAccount.accountReferenceId
    ) {
      return undefined;
    }

    if (!priceQuote) return undefined;

    const totalPrice =
      priceQuote.itinerary?.sellingPricing?.totalPricing.total.fiat ||
      undefined;

    if (!totalPrice) return undefined;

    let travelCreditAmount = Math.abs(creditToApply?.amount?.amount || 0);

    if (travelCreditAmount > totalPrice.value) {
      travelCreditAmount = totalPrice.value;
    }

    const cardPaymentAmount =
      totalPrice.value -
      (rewardsPaymentInFiatCurrency?.value || 0) -
      travelCreditAmount;

    if (cardPaymentAmount <= 0) {
      return undefined;
    }

    const multiplier = VXB_FLIGHTS_MULTIPLIER;

    const isCashRewards = rewardsAccount.rewardsBalance.currencyDescription
      ?.toLowerCase()
      .includes("cash");

    const earnAmount = isCashRewards
      ? cardPaymentAmount * (multiplier / 100)
      : cardPaymentAmount * multiplier;

    return {
      value: earnAmount,
      currency: rewardsAccount.rewardsBalance.currency,
      currencyDescription:
        rewardsAccount.rewardsBalance.currencyDescription || "miles",
    };
  }
);

export const getRewardsAccountsReferenceIds = createSelector(
  getRewardsAccounts,
  (rewardsAccounts): string[] =>
    rewardsAccounts.map((account) => account.accountReferenceId)
);

// Corporate Travel
export const getUserIsPrimaryCardHolder = createSelector(
  getRewardsAccounts,
  (rewardsAccounts) =>
    rewardsAccounts.some((account) => account.customerAccountRole === "Primary")
);

export const getAllowRewardsRedemptionInAnyAccount = createSelector(
  getRewardsAccounts,
  (rewardsAccounts): boolean =>
    rewardsAccounts.some((account) => account.allowRewardsRedemption ?? true)
);
